/**
 * Churn-risk fusion engine — the explainable successor to the single
 * opaque health number.
 *
 * Fixes the auditor's findings head-on:
 *   • OPAQUE NUMBER → every fused score is the visible sum of seven
 *     per-dimension contributions (weight × score, momentum included);
 *   • NO TREND → each dimension carries its weekly trend + Δ vs last week,
 *     and `computeTrajectory` turns health history into direction + a
 *     linear projection;
 *   • NON-ACTIONABLE DRIVERS → `explainDrivers` renders the top-3 causal
 *     narratives ('Score -12: WAU down 40% over 3wk, champion idle 14d');
 *   • NO CROSS-MODULE FUSION → the seven dimensions deliberately span
 *     usage/activity, tickets/tasks, engagement, deal momentum, margin
 *     (billing) drift, deliverable slippage and champion activity;
 *   • NO PRECISION REPORTING → `toOutcomeContract` seals a falsifiable
 *     before-claim ("Acme renews by 2025-09-30 at >=90% ACV") and
 *     `verifyPredictionPrecision` reports hit-rate over CLOSED contracts
 *     only (INCONCLUSIVE never counts for or against).
 *
 * Deliberately PURE: no store, no clock reads unless the caller omits
 * `now`, no network. The agent narrative is an optional INPUT — when it is
 * absent the deterministic fallback path produces a stable, honest
 * narrative from the same drivers, so the engine never blocks on a worker.
 *
 * Only types/pure helpers are imported from the studied modules — none of
 * them are modified.
 */

import type { OutcomeContract } from '../agent/outcomeContract';
import type { Person, ProjectTask, Deliverable } from '../db/types';
import { lastTouchAt } from './score';
import { isDeliverableOverdue } from './deliverablesSummary';

const DAY_MS = 86_400_000;

// ===========================================================================
// Dimension schema
// ===========================================================================

/** The seven fused risk dimensions, in canonical order. */
export type RiskDimensionKey =
  | 'usage'
  | 'ticketVelocity'
  | 'engagementRecency'
  | 'dealStall'
  | 'marginDrift'
  | 'deliverableSlippage'
  | 'championActivity';

export const RISK_DIMENSION_KEYS: ReadonlyArray<RiskDimensionKey> = [
  'usage',
  'ticketVelocity',
  'engagementRecency',
  'dealStall',
  'marginDrift',
  'deliverableSlippage',
  'championActivity',
];

/** Human labels used by driver narratives + the card UI. */
export const RISK_DIMENSION_LABELS: Record<RiskDimensionKey, string> = {
  usage: 'Product usage',
  ticketVelocity: 'Ticket velocity',
  engagementRecency: 'Engagement recency',
  dealStall: 'Deal momentum',
  marginDrift: 'Margin drift',
  deliverableSlippage: 'Deliverable slippage',
  championActivity: 'Champion activity',
};

/**
 * Fusion weights. Sum to exactly 1.0 so a uniform input maps to itself
 * (all dims at 50 → fused 50) and every contribution is directly a share
 * of the 0–100 probability.
 */
export const RISK_WEIGHTS: Record<RiskDimensionKey, number> = {
  usage: 0.22,
  engagementRecency: 0.18,
  championActivity: 0.16,
  ticketVelocity: 0.12,
  deliverableSlippage: 0.12,
  dealStall: 0.1,
  marginDrift: 0.1,
};

/** One raw per-dimension signal the caller measured from REAL data. */
export interface DimensionSignal {
  /** 0–100 risk for this dimension (100 = worst). */
  score: number;
  /** Weekly risk readings, oldest first (used for Δ + momentum + sparkline). */
  trend?: number[];
  /** Human causal detail, e.g. 'WAU down 40% over 3wk'. Never fabricated. */
  detail?: string;
}

/** A fused, explainable dimension inside a ChurnRiskResult. */
export interface RiskDimension {
  key: RiskDimensionKey;
  /** 0–100 risk (100 = worst). */
  score: number;
  /** Fusion weight (all seven sum to 1.0). */
  weight: number;
  /** Risk delta vs the previous weekly reading (positive = worsening). */
  deltaVsLastWeek: number;
  /** Weekly risk readings, oldest first ([] when no history exists). */
  trend: number[];
  /** This dimension's share of churnProbability: weight×score (+momentum). */
  contribution: number;
  /** Causal detail carried from the signal, when the caller measured one. */
  detail?: string;
  /** True when the caller provided no signal (neutral placeholder). */
  missing: boolean;
}

// ===========================================================================
// Input / result schema
// ===========================================================================

export interface ChurnRiskInput {
  accountId: string;
  accountName?: string;
  /** Per-dimension measured signals; missing dims lower confidence. */
  dimensions: Partial<Record<RiskDimensionKey, DimensionSignal>>;
  /** Optional narrative from the real agent worker; omitted → deterministic. */
  agentNarrative?: string;
  /** Epoch ms of the account's renewal, when known (drives lead time). */
  renewalAt?: number;
  /** The account's real ACV/ARR in USD, when set (never invented). */
  accountValue?: number;
  /** Injected clock for determinism. */
  now?: number;
}

export interface ChurnRiskResult {
  accountId: string;
  accountName?: string;
  /** 0–100 fused churn probability (100 = certain churn signal). */
  churnProbability: number;
  /** 0–1 data-sufficiency confidence (coverage + trend depth). */
  confidence: number;
  /** Days of runway before the risk becomes irreversible. */
  leadTimeDays: number;
  /** All seven dimensions, canonical order, contributions itemized. */
  dimensions: RiskDimension[];
  /** Where the narrative came from — the honest label. */
  narrativeSource: 'agent' | 'deterministic';
  narrative: string;
  /** Epoch ms the result was computed at (window start for contracts). */
  computedAt: number;
  renewalAt?: number;
  accountValue?: number;
}

// ===========================================================================
// Fusion
// ===========================================================================

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Fuse the seven dimension signals into one explainable churn probability.
 * Pure and deterministic: same input → identical output, always.
 *
 * contribution(dim) = weight × score  +  weight × max(0, Δweek) × 0.5
 * (the momentum term: a dimension that is actively worsening contributes
 * more than a static one at the same level — trends matter, not snapshots).
 * churnProbability = clamp(Σ contributions, 0, 100), rounded.
 */
export function computeChurnRisk(input: ChurnRiskInput): ChurnRiskResult {
  const now = input.now ?? Date.now();

  let provided = 0;
  let hasDeepTrend = false;
  const dimensions: RiskDimension[] = RISK_DIMENSION_KEYS.map((key) => {
    const signal = input.dimensions[key];
    const weight = RISK_WEIGHTS[key];
    const missing = signal === undefined;
    if (!missing) provided++;
    const score = clamp(signal?.score ?? 0, 0, 100);
    const trend = (signal?.trend ?? []).map((t) => clamp(t, 0, 100));
    if (trend.length >= 3) hasDeepTrend = true;
    const deltaVsLastWeek =
      trend.length >= 2 ? round1(trend[trend.length - 1] - trend[trend.length - 2]) : 0;
    const momentum = weight * Math.max(0, deltaVsLastWeek) * 0.5;
    const contribution = round1(weight * score + momentum);
    return {
      key,
      score,
      weight,
      deltaVsLastWeek,
      trend,
      contribution,
      ...(signal?.detail !== undefined ? { detail: signal.detail } : {}),
      missing,
    };
  });

  const churnProbability = Math.round(
    clamp(
      dimensions.reduce((sum, d) => sum + d.contribution, 0),
      0,
      100,
    ),
  );

  // Confidence is data sufficiency, never bravado: coverage of the seven
  // dimensions plus a bonus when at least one real weekly trend exists.
  const coverage = provided / RISK_DIMENSION_KEYS.length;
  const confidence = round1(clamp(0.35 + 0.5 * coverage + (hasDeepTrend ? 0.1 : 0), 0, 0.95) * 10) / 10;

  // Lead time: real renewal runway when known; otherwise the heuristic that
  // a hotter account leaves less time to act.
  const leadTimeDays =
    input.renewalAt !== undefined
      ? Math.max(0, Math.round((input.renewalAt - now) / DAY_MS))
      : clamp(Math.round((100 - churnProbability) * 1.2), 7, 120);

  const base: Omit<ChurnRiskResult, 'narrative' | 'narrativeSource'> = {
    accountId: input.accountId,
    ...(input.accountName !== undefined ? { accountName: input.accountName } : {}),
    churnProbability,
    confidence,
    leadTimeDays,
    dimensions,
    computedAt: now,
    ...(input.renewalAt !== undefined ? { renewalAt: input.renewalAt } : {}),
    ...(input.accountValue !== undefined ? { accountValue: input.accountValue } : {}),
  };

  // Deterministic fallback path: with no agent narrative available, the
  // narrative is generated from the same drivers — stable and honest.
  const agentText = input.agentNarrative?.trim();
  if (agentText) {
    return { ...base, narrativeSource: 'agent', narrative: agentText };
  }
  return {
    ...base,
    narrativeSource: 'deterministic',
    narrative: deterministicNarrative(base as ChurnRiskResult),
  };
}

// ===========================================================================
// Drivers — the top-3 causal explanations
// ===========================================================================

export interface DriverNarrative {
  key: RiskDimensionKey;
  /** Share of the churn probability this driver carries. */
  contribution: number;
  deltaVsLastWeek: number;
  /** Human-readable causal string, ready for the card. */
  text: string;
}

function deltaClause(d: RiskDimension): string {
  if (d.deltaVsLastWeek > 0) return `, up ${d.deltaVsLastWeek} vs last week`;
  if (d.deltaVsLastWeek < 0) return `, down ${Math.abs(d.deltaVsLastWeek)} vs last week`;
  return '';
}

function driverText(d: RiskDimension): string {
  const cause = d.detail ?? `${RISK_DIMENSION_LABELS[d.key]} risk ${d.score}/100${deltaClause(d)}`;
  return `Score -${Math.round(d.contribution)}: ${cause}`;
}

/**
 * The exactly-top-3 drivers by contribution, heaviest first — the causal
 * "why" a CS owner acts on. Deterministic tie-break: canonical dim order.
 */
export function explainDrivers(result: ChurnRiskResult): DriverNarrative[] {
  return [...result.dimensions]
    .sort(
      (a, b) =>
        b.contribution - a.contribution ||
        RISK_DIMENSION_KEYS.indexOf(a.key) - RISK_DIMENSION_KEYS.indexOf(b.key),
    )
    .slice(0, 3)
    .map((d) => ({
      key: d.key,
      contribution: d.contribution,
      deltaVsLastWeek: d.deltaVsLastWeek,
      text: driverText(d),
    }));
}

/** The stable fallback narrative — same drivers, no worker required. */
function deterministicNarrative(result: ChurnRiskResult): string {
  const drivers = explainDrivers(result)
    .filter((d) => d.contribution > 0)
    .map((d) => d.text);
  const head = `Churn risk ${result.churnProbability}/100 (confidence ${Math.round(result.confidence * 100)}%, ~${result.leadTimeDays}d runway).`;
  return drivers.length > 0 ? `${head} Top drivers — ${drivers.join(' · ')}` : `${head} No active risk drivers.`;
}

// ===========================================================================
// Trajectory — trend/delta over the health history
// ===========================================================================

/**
 * Minimal snapshot shape — structurally satisfied by the persisted
 * `CustomerHealthSnapshot` rows (db/healthHistory.ts) without importing
 * the store.
 */
export interface HealthSnapshot {
  /** Local calendar date key (YYYY-MM-DD), one point per day. */
  date: string;
  /** 0–100 health score for that day (higher = healthier). */
  score: number;
}

export type TrajectoryDirection = 'up' | 'down' | 'flat';

export interface Trajectory {
  /** Health scores oldest → newest. */
  series: number[];
  /** Newest minus oldest — the whole-window movement. */
  delta: number;
  direction: TrajectoryDirection;
  /** Linear projection one step past the newest reading, clamped 0–100. */
  projectedScore: number;
}

/**
 * Turn daily health snapshots into a trajectory: sorted series, total
 * delta, direction, and a one-step linear projection. An 82→61 slide is
 * `down` and projects LOWER than 61 — the early-warning the flat number hid.
 */
export function computeTrajectory(history: HealthSnapshot[]): Trajectory {
  const series = [...history]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((s) => s.score);

  if (series.length === 0) {
    return { series: [], delta: 0, direction: 'flat', projectedScore: 0 };
  }
  if (series.length === 1) {
    return { series, delta: 0, direction: 'flat', projectedScore: series[0] };
  }

  const first = series[0];
  const last = series[series.length - 1];
  const delta = last - first;
  const slope = delta / (series.length - 1);
  const direction: TrajectoryDirection = slope < -1 ? 'down' : slope > 1 ? 'up' : 'flat';
  const projectedScore = Math.round(clamp(last + slope, 0, 100));
  return { series, delta, direction, projectedScore };
}

// ===========================================================================
// Outcome contracts — the falsifiable predict-then-verify loop
// ===========================================================================

/** Renewal must retain at least this fraction of ACV to count as a save. */
export const ACV_RETENTION_THRESHOLD = 0.9;

/** Default claim window when no renewal date is known: the lead-time runway. */
export interface ChurnOutcomeContract extends OutcomeContract {
  /** The falsifiable human claim, e.g. 'Acme renews by 2025-09-30 at >=90% ACV'. */
  statement: string;
  /** Epoch ms the renewal must land by (== predicate window end). */
  renewalAt: number;
  /** Minimum ACV retention fraction for the claim to verify. */
  acvThreshold: number;
}

function isoDateOf(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

/**
 * Seal a churn-risk result into a falsifiable OutcomeContract: a named
 * renewal deadline + an ACV retention threshold + the dollars genuinely at
 * stake (the account's own value — 0 when unset, never invented). The
 * predicate is a `health_delta` claim over [computedAt, renewalAt] with
 * target 0: health must not have declined by the renewal for the save to
 * verify. Passes `validateOutcomeContract` as-is.
 */
export function toOutcomeContract(accountId: string, result: ChurnRiskResult): ChurnOutcomeContract {
  const renewalAt = result.renewalAt ?? result.computedAt + result.leadTimeDays * DAY_MS;
  const name = result.accountName ?? accountId;
  return {
    proposalId: `churn-risk:${accountId}:${isoDateOf(result.computedAt)}`,
    actionType: 'churn_risk_watch',
    predicate: {
      kind: 'health_delta',
      window: { start: result.computedAt, end: renewalAt },
      target: 0,
    },
    dollarsAtStake: result.accountValue ?? 0,
    statement: `${name} renews by ${isoDateOf(renewalAt)} at >=${Math.round(ACV_RETENTION_THRESHOLD * 100)}% ACV`,
    renewalAt,
    acvThreshold: ACV_RETENTION_THRESHOLD,
  };
}

// ===========================================================================
// Prediction precision — the honest hit-rate report
// ===========================================================================

/** The verdict shape the verifier renders (structural — no import cycle). */
export interface ContractOutcome {
  proposalId: string;
  verdict: 'VERIFIED' | 'MISSED' | 'INCONCLUSIVE';
}

export interface PrecisionBucket {
  hitRate: number;
  sampleSize: number;
}

export interface PredictionPrecision {
  /** VERIFIED / (VERIFIED + MISSED). 0 when nothing has closed. */
  hitRate: number;
  /** Closed contracts only — INCONCLUSIVE never counts for or against. */
  sampleSize: number;
  /** Precision bucketed by claim lead time (window length). */
  byLeadTime: Record<'<=30d' | '31-90d' | '>90d', PrecisionBucket>;
}

function leadBucketOf(contract: OutcomeContract): '<=30d' | '31-90d' | '>90d' {
  const days = (contract.predicate.window.end - contract.predicate.window.start) / DAY_MS;
  if (days <= 30) return '<=30d';
  if (days <= 90) return '31-90d';
  return '>90d';
}

/**
 * Compute prediction precision over CLOSED contracts (VERIFIED or MISSED).
 * Open/INCONCLUSIVE claims are excluded entirely — a model may not grade
 * itself on questions reality hasn't answered yet.
 */
export function verifyPredictionPrecision(
  contracts: OutcomeContract[],
  outcomes: ContractOutcome[],
): PredictionPrecision {
  const verdictById = new Map<string, ContractOutcome['verdict']>();
  for (const o of outcomes) verdictById.set(o.proposalId, o.verdict);

  const byLeadTime: PredictionPrecision['byLeadTime'] = {
    '<=30d': { hitRate: 0, sampleSize: 0 },
    '31-90d': { hitRate: 0, sampleSize: 0 },
    '>90d': { hitRate: 0, sampleSize: 0 },
  };
  const hitsByBucket: Record<string, number> = { '<=30d': 0, '31-90d': 0, '>90d': 0 };

  let closed = 0;
  let hits = 0;
  for (const c of contracts) {
    const verdict = verdictById.get(c.proposalId);
    if (verdict !== 'VERIFIED' && verdict !== 'MISSED') continue;
    closed++;
    const bucket = leadBucketOf(c);
    byLeadTime[bucket].sampleSize++;
    if (verdict === 'VERIFIED') {
      hits++;
      hitsByBucket[bucket]++;
    }
  }
  for (const key of ['<=30d', '31-90d', '>90d'] as const) {
    const b = byLeadTime[key];
    b.hitRate = b.sampleSize > 0 ? hitsByBucket[key] / b.sampleSize : 0;
  }
  return { hitRate: closed > 0 ? hits / closed : 0, sampleSize: closed, byLeadTime };
}

// ===========================================================================
// Real-record adapter — builds ChurnRiskInput from live store rows
// ===========================================================================

export interface DeriveChurnContext {
  tasks?: ProjectTask[];
  /** First-class deliverables tied to the account (db/deliverables). */
  deliverables?: Deliverable[];
  /** Daily health snapshots for THIS person (db/healthHistory rows). */
  history?: HealthSnapshot[];
  now?: number;
}

function daysSinceIso(iso: string | undefined, now: number): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, (now - t) / DAY_MS);
}

/** Weekly risk trend (oldest first) from health history: risk = 100 − health. */
function riskTrendFromHistory(history: HealthSnapshot[]): number[] {
  return [...history]
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-8)
    .map((s) => clamp(100 - s.score, 0, 100));
}

/**
 * Derive the seven dimension signals from a customer's REAL record — every
 * number traces to rows on the record (activity, tasks, billing,
 * deliverables, health history). Nothing is fabricated: a dimension with
 * no evidence is simply omitted, which lowers confidence instead of
 * inventing a reading.
 */
export function deriveChurnInput(person: Person, ctx: DeriveChurnContext = {}): ChurnRiskInput {
  const now = ctx.now ?? Date.now();
  const dimensions: Partial<Record<RiskDimensionKey, DimensionSignal>> = {};

  // usage — activity velocity: touches in the last 30d vs the prior 30d.
  const activity = person.activity ?? [];
  const touchesRecent = activity.filter((a) => {
    const d = daysSinceIso(a.at, now);
    return d !== undefined && d <= 30;
  }).length;
  const touchesPrior = activity.filter((a) => {
    const d = daysSinceIso(a.at, now);
    return d !== undefined && d > 30 && d <= 60;
  }).length;
  if (activity.length > 0) {
    let score = 30;
    let detail = `${touchesRecent} touch${touchesRecent === 1 ? '' : 'es'} in 30d`;
    if (touchesPrior > 0 && touchesRecent < touchesPrior) {
      const dropPct = Math.round(((touchesPrior - touchesRecent) / touchesPrior) * 100);
      score = clamp(30 + dropPct * 0.6, 0, 100);
      detail = `activity down ${dropPct}% vs prior 30d (${touchesPrior}→${touchesRecent})`;
    } else if (touchesRecent > touchesPrior) {
      score = 15;
      detail = `activity up vs prior 30d (${touchesPrior}→${touchesRecent})`;
    }
    const trend = riskTrendFromHistory(ctx.history ?? []);
    dimensions.usage = { score: Math.round(score), detail, ...(trend.length > 0 ? { trend } : {}) };
  }

  // ticketVelocity — open/overdue work items tied to this customer.
  const myTasks = (ctx.tasks ?? []).filter((t) => t.personId === person.id && t.status !== 'done');
  if (myTasks.length > 0) {
    const overdue = myTasks.filter((t) => {
      const d = t.dueDate ? Date.parse(t.dueDate) : NaN;
      return Number.isFinite(d) && d < now;
    }).length;
    dimensions.ticketVelocity = {
      score: clamp(Math.round(20 + overdue * 25 + myTasks.length * 5), 0, 100),
      detail: `${myTasks.length} open task${myTasks.length === 1 ? '' : 's'}, ${overdue} overdue`,
    };
  }

  // engagementRecency — silence since the freshest touch.
  const quietDays = daysSinceIso(lastTouchAt(person), now);
  if (quietDays !== undefined) {
    dimensions.engagementRecency = {
      score: clamp(Math.round(quietDays * 1.2), 0, 100),
      detail: quietDays >= 1 ? `quiet ${Math.round(quietDays)}d` : 'touched today',
    };
  }

  // dealStall — renewal proximity / lapse.
  if (person.renewalDate) {
    const t = Date.parse(person.renewalDate);
    if (Number.isFinite(t)) {
      const days = Math.round((t - now) / DAY_MS);
      const score = days < 0 ? 90 : days <= 14 ? 70 : days <= 30 ? 50 : days <= 90 ? 25 : 10;
      dimensions.dealStall = {
        score,
        detail: days < 0 ? `renewal lapsed ${Math.abs(days)}d ago` : `renewal in ${days}d`,
      };
    }
  }

  // marginDrift — past-due, unpaid billing milestones (cash drift).
  const overdueInvoices = (person.billingDates ?? []).filter((bd) => {
    const t = Date.parse(bd.date);
    return Number.isFinite(t) && t < now && (bd.status ?? 'scheduled') !== 'paid';
  }).length;
  if ((person.billingDates ?? []).length > 0) {
    dimensions.marginDrift = {
      score: clamp(overdueInvoices * 35, 0, 100),
      detail:
        overdueInvoices > 0
          ? `${overdueInvoices} past-due invoice${overdueInvoices === 1 ? '' : 's'}`
          : 'billing current',
    };
  }

  // deliverableSlippage — blocked / overdue commitments (embedded + first-class).
  const embedded = person.deliverables ?? [];
  const firstClass = ctx.deliverables ?? [];
  const totalDeliverables = embedded.length + firstClass.length;
  if (totalDeliverables > 0) {
    const blocked = embedded.filter((d) => d.status === 'blocked').length;
    const overdueEmbedded = embedded.filter((d) => {
      if (d.status === 'delivered') return false;
      const t = d.dueDate ? Date.parse(d.dueDate) : NaN;
      return Number.isFinite(t) && t < now;
    }).length;
    const overdueFirstClass = firstClass.filter((d) => isDeliverableOverdue(d, now)).length;
    const slipping = blocked + overdueEmbedded + overdueFirstClass;
    dimensions.deliverableSlippage = {
      score: clamp(Math.round((slipping / totalDeliverables) * 100), 0, 100),
      detail: `${slipping} of ${totalDeliverables} deliverable${totalDeliverables === 1 ? '' : 's'} slipping`,
    };
  }

  // championActivity — days since the champion (any non-system human touch)
  // was last active on the record.
  const humanTouches = activity.filter((a) => a.kind !== 'system');
  if (humanTouches.length > 0) {
    const lastHuman = humanTouches.reduce((max, a) => {
      const t = Date.parse(a.at);
      return Number.isFinite(t) && t > max ? t : max;
    }, 0);
    const idleDays = Math.max(0, Math.round((now - lastHuman) / DAY_MS));
    const actor = humanTouches[humanTouches.length - 1].actor;
    dimensions.championActivity = {
      score: clamp(idleDays * 3, 0, 100),
      detail: `champion${actor ? ` ${actor}` : ''} idle ${idleDays}d`,
    };
  }

  const renewalTs = person.renewalDate ? Date.parse(person.renewalDate) : NaN;
  return {
    accountId: person.id,
    accountName: person.name,
    dimensions,
    ...(Number.isFinite(renewalTs) ? { renewalAt: renewalTs } : {}),
    ...(person.accountValue !== undefined ? { accountValue: person.accountValue } : {}),
    now,
  };
}
